/**
 * Environment + auth indirection for the Sessions REST clients.
 *
 * Kept separate from the client modules so jest can mock this file — the
 * clients themselves stay free of `import.meta` (which babel-jest cannot
 * transform) and of direct `aws-amplify` imports.
 */

import { fetchAuthSession } from 'aws-amplify/auth'
import { fetchNativeLivePingCredentials } from './native-live-ping-credential-fetch'

/** True when a live Sessions API base URL is configured. */
export function isSessionsApiConfigured(): boolean {
  return Boolean(import.meta.env.VITE_SESSIONS_API_URL)
}

/** Normalized Sessions API base URL. Throws when unset (mock mode). */
export function getSessionsApiBaseUrl(): string {
  const url = import.meta.env.VITE_SESSIONS_API_URL ?? ''
  if (!url) {
    throw new Error('VITE_SESSIONS_API_URL is not set')
  }
  return String(url).replace(/\/$/, '')
}

/**
 * Cognito **ID token** for API Gateway's user-pool authorizer (not the access
 * token). Returns null when signed out.
 */
export async function getSessionsAuthToken(): Promise<string | null> {
  const session = await fetchAuthSession()
  return session.tokens?.idToken?.toString() ?? null
}

/** Credentials handed to the native layer for live journey pings while backgrounded. */
export interface NativeLivePingCredentials {
  apiBaseUrl: string
  /** Cognito ID token (what the Sessions API authorizer expects). Never a refresh token. */
  idToken: string
  /** Token `exp`, epoch ms. */
  expiresAt: number
}

/**
 * A token handed to native must outlast a typical drive with the app in the
 * background (JS cannot refresh it then): below this remaining lifetime the
 * ID token is refreshed first.
 */
export const NATIVE_TOKEN_MIN_REMAINING_MS = 30 * 60_000
/**
 * Going to the background is the last moment JS reliably runs before a long
 * drive: refresh unless the token still has this long (ID tokens last 60 min).
 */
export const NATIVE_TOKEN_BACKGROUND_MIN_REMAINING_MS = 50 * 60_000

/**
 * Sessions API base URL + a short-lived Cognito ID token with its expiry, for
 * the native background-location layer (docs/sessions-location-tracking.md).
 * Only the ID token leaves JS — the refresh token never does. Null when the
 * API is not configured or the user is signed out.
 */
export async function getNativeLivePingCredentials(
  now: number = Date.now(),
  minRemainingMs: number = NATIVE_TOKEN_MIN_REMAINING_MS,
): Promise<NativeLivePingCredentials | null> {
  if (!isSessionsApiConfigured()) return null
  return fetchNativeLivePingCredentials({
    fetchSession: (options) => fetchAuthSession(options),
    apiBaseUrl: getSessionsApiBaseUrl(),
    now,
    minRemainingMs,
  })
}
